export interface QuizOption {
  value: string;
  text: string;
}

export interface QuizQuestion {
  id: number;
  question: string;
  options: QuizOption[];
  emoji: string;
  isCheckbox?: boolean;
}

export interface QuizResultTier {
  key: string;
  title: string;
  emoji: string;
  summary: string;
  detail: string;
  symptomNote?: string;
}

export interface QuizData {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  emoji: string;
  gradientFrom: string;
  gradientTo: string;
  questions: QuizQuestion[];
  resultTiers: QuizResultTier[];
  nextSteps: string[];
  importantNote?: string;
  checkboxQuestionIds: number[];
  romyStarters: string[];
}

const adrenalFatigueQuiz: QuizData = {
  slug: "adrenal-fatigue",
  title: "Is It \"Adrenal Fatigue\" — or a Stress-Burnout Pattern?",
  subtitle: "A Self-Check for Chronic Stress & HPA-Axis Dysregulation",
  description:
    "You may have heard the term adrenal fatigue used to describe feeling constantly exhausted, wired-but-tired, and unable to bounce back from stress. While \"adrenal fatigue\" isn't a formal medical diagnosis, the symptom patterns people describe are very real — and often reflect chronic stress, burnout, or HPA-axis dysregulation.\n\nThis quiz isn't a diagnosis. It's a pattern-spotting tool to help you understand whether chronic stress physiology could be contributing to how you feel — and whether deeper evaluation is warranted.",
  emoji: "😴",
  gradientFrom: "from-orange-500",
  gradientTo: "to-pink-500",
  questions: [
    {
      id: 1,
      question: "How do you feel when you wake up in the morning?",
      options: [
        { value: "A", text: "Rested and ready to go" },
        { value: "B", text: "A little groggy, but functional" },
        { value: "C", text: "Exhausted, even after a full night's sleep" },
        { value: "D", text: "Like I was hit by a truck — mornings are the hardest part of my day" },
      ],
      emoji: "😴",
    },
    {
      id: 2,
      question: "How do you rely on caffeine or stimulants?",
      options: [
        { value: "A", text: "I don't really need them" },
        { value: "B", text: "One coffee helps" },
        { value: "C", text: "I need multiple cups to function" },
        { value: "D", text: "Caffeine helps briefly, then makes me crash or feel anxious" },
      ],
      emoji: "☕",
    },
    {
      id: 3,
      question: "Which best describes your energy pattern?",
      options: [
        { value: "A", text: "Steady throughout the day" },
        { value: "B", text: "A small afternoon dip" },
        { value: "C", text: "Waves of exhaustion with short bursts of energy" },
        { value: "D", text: "Tired all day, then wired at night" },
      ],
      emoji: "⚡",
    },
    {
      id: 4,
      question: "How does stress affect you now compared to the past?",
      options: [
        { value: "A", text: "I bounce back quickly" },
        { value: "B", text: "It takes me longer than it used to" },
        { value: "C", text: "Stress feels overwhelming or depleting" },
        { value: "D", text: "Even small stressors feel like too much" },
      ],
      emoji: "🧠",
    },
    {
      id: 5,
      question: "How is your sleep?",
      options: [
        { value: "A", text: "I fall asleep easily and stay asleep" },
        { value: "B", text: "I wake once or twice" },
        { value: "C", text: "Trouble falling asleep or waking too early" },
        { value: "D", text: "Exhausted but unable to sleep deeply or consistently" },
      ],
      emoji: "🌙",
    },
    {
      id: 6,
      question: "Do you notice any of the following? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Salt cravings" },
        { value: "symptoms", text: "Dizziness when standing" },
        { value: "symptoms", text: "Shakiness or weakness" },
        { value: "symptoms", text: "Sensitivity to noise, light, or stress" },
        { value: "symptoms", text: "Feeling worse when you skip meals" },
      ],
      emoji: "🧂",
      isCheckbox: true,
    },
    {
      id: 7,
      question: "How does your body respond to exercise?",
      options: [
        { value: "A", text: "I feel energized afterward" },
        { value: "B", text: "Mild fatigue but quick recovery" },
        { value: "C", text: "Exercise wipes me out" },
        { value: "D", text: "I crash for days after physical or mental exertion" },
      ],
      emoji: "🧍‍♀️",
    },
    {
      id: 8,
      question: "What about mood and cognition?",
      options: [
        { value: "A", text: "Mentally sharp" },
        { value: "B", text: "Mild brain fog" },
        { value: "C", text: "Trouble concentrating or remembering things" },
        { value: "D", text: "Anxiety, low mood, or emotional fragility when tired" },
      ],
      emoji: "🧠",
    },
    {
      id: 9,
      question: "How long have you been feeling this way?",
      options: [
        { value: "A", text: "A few weeks" },
        { value: "B", text: "A few months" },
        { value: "C", text: "1–2 years" },
        { value: "D", text: "Several years — it feels like my \"new normal\"" },
      ],
      emoji: "🕰️",
    },
    {
      id: 10,
      question: "When you hear people describe \"adrenal fatigue,\" you feel:",
      options: [
        { value: "A", text: "Curious but unsure" },
        { value: "B", text: "Somewhat seen" },
        { value: "C", text: "Like it explains a lot" },
        { value: "D", text: "Like it perfectly describes what I've been living with" },
      ],
      emoji: "🔍",
    },
  ],
  resultTiers: [
    {
      key: "A",
      title: "Mostly A's",
      emoji: "🌱",
      summary:
        "Your answers don't suggest significant stress-related dysregulation right now. If fatigue persists, routine medical causes should still be ruled out.",
      detail:
        "Even without a strong stress pattern, persistent tiredness can point to iron deficiency, thyroid issues, or sleep disorders. Keep listening to your body and track any changes.",
    },
    {
      key: "B",
      title: "Mostly B's",
      emoji: "🌸",
      summary:
        "You may be experiencing early burnout or stress strain. Supporting sleep, recovery, and boundaries now can prevent deeper depletion.",
      detail:
        "Catching stress patterns early gives you the best chance to course-correct. Prioritize rest, set firmer boundaries around energy-draining commitments, and consider a check-in with a provider who understands stress physiology.",
    },
    {
      key: "C",
      title: "Mostly C's",
      emoji: "🔥",
      summary:
        "Your responses reflect patterns commonly described as \"adrenal fatigue\" — often linked to chronic stress, under-recovery, or nervous system overload. A more comprehensive evaluation may be helpful.",
      detail:
        "These patterns suggest your stress-response system has been running on high for a while. Functional or integrative providers can test cortisol rhythms, DHEA-S, and other markers that standard labs often skip.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which may further support a chronic stress or HPA-axis pattern.",
    },
    {
      key: "D",
      title: "Mostly D's",
      emoji: "🚨",
      summary:
        "Your symptoms strongly suggest significant stress-related physiological dysregulation. Many people with similar patterns feel dismissed or are told \"labs are normal.\" You deserve care that looks beyond surface-level tests.",
      detail:
        "A D-dominant pattern often means the body has been in survival mode for an extended time. Seek providers experienced in HPA-axis dysfunction, nervous system regulation, and recovery-focused care. You are not imagining this.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which reinforce a significant stress-response pattern worth investigating further.",
    },
  ],
  nextSteps: [
    "Rule out medical causes of fatigue (iron deficiency, thyroid disease, sleep disorders, depression, autoimmune conditions)",
    "Track energy, sleep, stress load, and crashes over time",
    "Seek providers who understand stress physiology, nervous system regulation, and recovery",
    "Be cautious with supplements or protocols claiming to \"fix\" adrenal fatigue",
  ],
  importantNote:
    "If you had true adrenal failure (Addison's disease), you would be very sick — and it requires urgent medical care. This quiz is about chronic stress patterns, not adrenal gland failure.",
  checkboxQuestionIds: [6],
  romyStarters: [
    "I just took the adrenal fatigue quiz and scored mostly {tier}s. Can you help me understand what HPA-axis dysregulation means?",
    "What lifestyle changes can help with chronic stress and burnout recovery?",
    "What lab tests should I ask my doctor about for fatigue and stress patterns?",
  ],
};

const endometriosisQuiz: QuizData = {
  slug: "endometriosis",
  title: "Could It Be Endometriosis?",
  subtitle: "A \"Trust Your Body\" Self-Check Quiz",
  description:
    "Endometriosis affects an estimated 1 in 10 people with ovaries, yet many go 7–10 years before being diagnosed. Symptoms are often dismissed as \"normal period pain,\" stress, or IBS — but your experience matters.\n\nThis quiz isn't a diagnosis. It's a pattern-spotting tool to help you understand whether endometriosis is worth discussing further with a healthcare provider.\n\nGrab a pen, take a breath, and answer honestly.",
  emoji: "🩸",
  gradientFrom: "from-pink-500",
  gradientTo: "to-orange-500",
  questions: [
    {
      id: 1,
      question: "How would you describe your period pain?",
      options: [
        { value: "A", text: "Mild discomfort, manageable with no meds" },
        { value: "B", text: "Cramping that responds to over-the-counter pain relievers" },
        { value: "C", text: "Pain that regularly interferes with work, school, or plans" },
        { value: "D", text: "Pain so severe I've missed commitments, curled up in bed, or felt nauseated/faint" },
      ],
      emoji: "🩸",
    },
    {
      id: 2,
      question: "When do you experience pain?",
      options: [
        { value: "A", text: "Only during my period" },
        { value: "B", text: "Mostly the first 1–2 days of bleeding" },
        { value: "C", text: "Before my period starts and during it" },
        { value: "D", text: "Throughout the month, even when I'm not bleeding" },
      ],
      emoji: "📅",
    },
    {
      id: 3,
      question:
        "Have you ever been told your pain is \"normal\" or that you just have a \"low pain tolerance\"?",
      options: [
        { value: "A", text: "No" },
        { value: "B", text: "Once or twice" },
        { value: "C", text: "Yes, by multiple providers" },
        { value: "D", text: "Yes — and it delayed me seeking more help" },
      ],
      emoji: "💊",
    },
    {
      id: 4,
      question: "Do you experience pain with or after sex?",
      options: [
        { value: "A", text: "No" },
        { value: "B", text: "Occasionally" },
        { value: "C", text: "Frequently" },
        { value: "D", text: "Almost always, or I avoid sex because of pain" },
      ],
      emoji: "💥",
    },
    {
      id: 5,
      question:
        "Do you have bowel or bladder symptoms that worsen around your period? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Pain with bowel movements" },
        { value: "symptoms", text: "Diarrhea or constipation during your cycle" },
        { value: "symptoms", text: "Rectal pain or pressure" },
        { value: "symptoms", text: "Pain with urination during your period" },
        { value: "symptoms", text: "None of the above" },
      ],
      emoji: "🚽",
      isCheckbox: true,
    },
    {
      id: 6,
      question: "How does your pain respond to typical treatments?",
      options: [
        { value: "A", text: "NSAIDs work well" },
        { value: "B", text: "Hormonal birth control helps somewhat" },
        { value: "C", text: "Relief is inconsistent or short-lived" },
        { value: "D", text: "Nothing really helps — or side effects are intolerable" },
      ],
      emoji: "🌡️",
    },
    {
      id: 7,
      question: "Do your symptoms affect your mental or emotional health?",
      options: [
        { value: "A", text: "Not really" },
        { value: "B", text: "Occasionally" },
        { value: "C", text: "I feel anxious or down anticipating my cycle" },
        { value: "D", text: "I feel dismissed, exhausted, or emotionally worn down" },
      ],
      emoji: "🧠",
    },
    {
      id: 8,
      question: "Have you had any of the following experiences? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Years of unexplained pelvic pain" },
        { value: "symptoms", text: "A \"clear\" ultrasound despite severe symptoms" },
        { value: "symptoms", text: "IBS, migraines, painful periods, or fatigue labeled as unrelated" },
        { value: "symptoms", text: "Trouble getting pregnant or unexplained infertility" },
        { value: "symptoms", text: "Being told surgery is the only way to know what's wrong" },
      ],
      emoji: "👩‍⚕️",
      isCheckbox: true,
    },
    {
      id: 9,
      question: "How long have these symptoms been part of your life?",
      options: [
        { value: "A", text: "Less than 6 months" },
        { value: "B", text: "6–12 months" },
        { value: "C", text: "1–3 years" },
        { value: "D", text: "3+ years" },
      ],
      emoji: "⏳",
    },
    {
      id: 10,
      question: "When you read about endometriosis, what do you feel?",
      options: [
        { value: "A", text: "Curious" },
        { value: "B", text: "Unsure" },
        { value: "C", text: "Uncomfortably seen" },
        { value: "D", text: "Like someone finally put words to my experience" },
      ],
      emoji: "🔍",
    },
  ],
  resultTiers: [
    {
      key: "A",
      title: "Mostly A's",
      emoji: "🌱",
      summary:
        "Your symptoms don't strongly suggest endometriosis right now. Still, any pain that concerns you is worth tracking and discussing with a provider.",
      detail:
        "Even mild symptoms deserve attention if they change over time. Keep a symptom journal and revisit this quiz if things shift.",
    },
    {
      key: "B",
      title: "Mostly B's",
      emoji: "🌸",
      summary:
        "You may be experiencing cycle-related pain that deserves monitoring. If symptoms worsen or interfere with daily life, further evaluation could be helpful.",
      detail:
        "Cycle-related pain that responds to OTC meds is common, but worsening patterns can signal something deeper. Tracking when and where you feel pain will help a provider assess whether further workup is warranted.",
    },
    {
      key: "C",
      title: "Mostly C's",
      emoji: "🔥",
      summary:
        "Your answers show patterns commonly reported by people with endometriosis. You may benefit from tracking symptoms over time and preparing specific questions for a clinician.",
      detail:
        "C-dominant patterns often reflect moderate but persistent symptom burden — the kind that gets normalized over time. Consider seeking an endometriosis-informed provider who won't dismiss your experience.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which are commonly associated with endometriosis and worth mentioning to your provider.",
    },
    {
      key: "D",
      title: "Mostly D's",
      emoji: "🚨",
      summary:
        "Your experiences strongly overlap with classic endometriosis symptom patterns. Many people with similar answers report years of delayed diagnosis. You deserve to be taken seriously.",
      detail:
        "A D-dominant pattern points to significant symptom burden that has likely been affecting your quality of life for a while. Endometriosis-specialist care (not just general OB-GYN) can make a real difference in getting answers and relief.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which strengthen the case for a thorough endometriosis evaluation.",
    },
  ],
  nextSteps: [
    "Start tracking pain, timing, triggers, and impact on daily life",
    "Bring concrete examples to your next appointment",
    "Ask about endometriosis-informed care (not all providers are trained equally)",
    "Remember: Normal periods do not ruin lives",
    "If this quiz resonated deeply, you are not alone — and you are not imagining this",
  ],
  checkboxQuestionIds: [5, 8],
  romyStarters: [
    "I just took the endometriosis quiz and scored mostly {tier}s. What should I know about getting evaluated?",
    "How do I find an endometriosis specialist and what questions should I ask?",
    "Can you help me prepare a symptom summary for my next doctor's appointment?",
  ],
};

const pcosQuiz: QuizData = {
  slug: "pcos",
  title: "Could It Be PCOS?",
  subtitle: "A Self-Check for Polycystic Ovary Syndrome",
  description:
    "Polycystic ovary syndrome (PCOS) affects up to 1 in 10 people with ovaries and is one of the most common hormonal disorders — yet it takes an average of 2+ years and 3+ doctors to get diagnosed. Symptoms like irregular periods, acne, hair changes, and weight struggles are often treated in isolation instead of being connected.\n\nThis quiz isn't a diagnosis. It's a pattern-spotting tool to help you see whether PCOS-related symptoms might be showing up together — and whether it's worth bringing up with a provider.",
  emoji: "🔬",
  gradientFrom: "from-purple-500",
  gradientTo: "to-pink-500",
  questions: [
    {
      id: 1,
      question: "How would you describe your menstrual cycle?",
      options: [
        { value: "A", text: "Regular — comes every 24–35 days" },
        { value: "B", text: "Slightly irregular — sometimes early or late by a week" },
        { value: "C", text: "Frequently irregular — cycles are 35–60+ days apart" },
        { value: "D", text: "Very irregular or absent — I can go months without a period" },
      ],
      emoji: "📅",
    },
    {
      id: 2,
      question: "Have you noticed changes in your hair growth?",
      options: [
        { value: "A", text: "No unusual hair growth" },
        { value: "B", text: "A few extra hairs on my chin or upper lip" },
        { value: "C", text: "Noticeable dark hair on my face, chest, or abdomen" },
        { value: "D", text: "Significant excess hair growth that I actively manage" },
      ],
      emoji: "💇",
    },
    {
      id: 3,
      question: "How would you describe your skin?",
      options: [
        { value: "A", text: "Generally clear with occasional breakouts" },
        { value: "B", text: "Mild acne, mostly around my period" },
        { value: "C", text: "Persistent acne along my jawline, chin, or back" },
        { value: "D", text: "Severe cystic acne that doesn't respond to typical treatments" },
      ],
      emoji: "✨",
    },
    {
      id: 4,
      question: "What's your experience with weight?",
      options: [
        { value: "A", text: "I maintain weight without much difficulty" },
        { value: "B", text: "I gain weight somewhat easily but can manage it" },
        { value: "C", text: "I gain weight easily, especially around my midsection, and it's hard to lose" },
        { value: "D", text: "Despite diet and exercise, weight loss feels nearly impossible" },
      ],
      emoji: "⚖️",
    },
    {
      id: 5,
      question: "Do you experience energy crashes or sugar cravings?",
      options: [
        { value: "A", text: "Rarely — my energy is fairly steady" },
        { value: "B", text: "Occasional afternoon slumps or cravings" },
        { value: "C", text: "Frequent energy crashes, especially after meals" },
        { value: "D", text: "Intense cravings, shakiness between meals, or feeling \"hangry\" often" },
      ],
      emoji: "🍬",
    },
    {
      id: 6,
      question: "Have you experienced any of the following? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Thinning hair on your head" },
        { value: "symptoms", text: "Dark patches of skin (neck, armpits, groin)" },
        { value: "symptoms", text: "Skin tags" },
        { value: "symptoms", text: "Difficulty getting pregnant" },
        { value: "symptoms", text: "Diagnosed with ovarian cysts" },
      ],
      emoji: "🔎",
      isCheckbox: true,
    },
    {
      id: 7,
      question: "How does your mood fluctuate?",
      options: [
        { value: "A", text: "Generally stable with minor fluctuations" },
        { value: "B", text: "Some mood swings around my period" },
        { value: "C", text: "Noticeable anxiety, irritability, or low mood that feels hormone-related" },
        { value: "D", text: "Significant mood struggles — depression, anxiety, or emotional overwhelm" },
      ],
      emoji: "🧠",
    },
    {
      id: 8,
      question: "Has anyone in your family been diagnosed with PCOS, type 2 diabetes, or insulin resistance?",
      options: [
        { value: "A", text: "Not that I know of" },
        { value: "B", text: "A distant relative" },
        { value: "C", text: "A parent or sibling has type 2 diabetes or insulin resistance" },
        { value: "D", text: "A parent or sibling has PCOS, or multiple family members have metabolic conditions" },
      ],
      emoji: "👨‍👩‍👧",
    },
    {
      id: 9,
      question: "Have you been told any of the following by a provider? (Check all that apply)",
      options: [
        { value: "symptoms", text: "\"Just lose weight and your periods will regulate\"" },
        { value: "symptoms", text: "\"Your labs are normal\" despite ongoing symptoms" },
        { value: "symptoms", text: "\"Go on birth control\" without further investigation" },
        { value: "symptoms", text: "\"You're too young to worry about this\"" },
      ],
      emoji: "👩‍⚕️",
      isCheckbox: true,
    },
    {
      id: 10,
      question: "How many of these symptom clusters do you relate to?",
      options: [
        { value: "A", text: "None or one — I'm just exploring" },
        { value: "B", text: "Two — a couple symptoms overlap" },
        { value: "C", text: "Three or four — several patterns connect" },
        { value: "D", text: "Five or more — this quiz is describing my life" },
      ],
      emoji: "🔬",
    },
  ],
  resultTiers: [
    {
      key: "A",
      title: "Mostly A's",
      emoji: "🌱",
      summary:
        "Your answers don't strongly suggest PCOS patterns right now. If you have specific concerns, they're still worth tracking and discussing.",
      detail:
        "PCOS can present subtly, and not everyone fits the \"textbook\" picture. If you notice symptoms emerging or clustering over time, revisit this quiz or talk to a provider who understands hormonal health.",
    },
    {
      key: "B",
      title: "Mostly B's",
      emoji: "🌸",
      summary:
        "You may have some early or mild hormonal patterns worth monitoring. Tracking symptoms over a few cycles can reveal whether a pattern is forming.",
      detail:
        "Mild irregularity, occasional skin or hair changes, and slight weight shifts can be normal — or they can be early signals. A baseline hormone panel (testosterone, DHEA-S, fasting insulin, AMH) could provide clarity.",
    },
    {
      key: "C",
      title: "Mostly C's",
      emoji: "🔥",
      summary:
        "Your symptom pattern overlaps meaningfully with PCOS. Several of your answers point to the hormonal and metabolic features commonly seen in this condition.",
      detail:
        "A C-dominant pattern suggests multiple PCOS hallmarks are present — irregular cycles, androgen-related symptoms, and possible insulin resistance. Seek a provider who will test beyond just an ultrasound: comprehensive hormone panels and metabolic markers matter.",
      symptomNote:
        "You reported {count} additional sign(s) ({examples}), which are commonly associated with PCOS and worth discussing with your provider.",
    },
    {
      key: "D",
      title: "Mostly D's",
      emoji: "🚨",
      summary:
        "Your answers strongly align with classic PCOS symptom clusters. Many people with similar patterns report being dismissed or undertreated for years. You deserve thorough evaluation.",
      detail:
        "A D-dominant pattern suggests significant hormonal and metabolic disruption consistent with PCOS. Seek a provider — ideally an endocrinologist or reproductive endocrinologist — who will do a full workup including androgen levels, insulin, glucose, and lipid panels. PCOS management goes far beyond birth control.",
      symptomNote:
        "You reported {count} additional sign(s) ({examples}), which reinforce a strong PCOS pattern that warrants comprehensive evaluation.",
    },
  ],
  nextSteps: [
    "Request a comprehensive hormone panel: total/free testosterone, DHEA-S, fasting insulin, glucose, AMH, and thyroid panel",
    "Track your cycles, symptoms, and any skin/hair changes over 3+ months",
    "Ask about insulin resistance testing — it's a core driver of PCOS that is often overlooked",
    "Seek providers experienced in PCOS who look beyond ultrasound alone",
  ],
  importantNote:
    "PCOS is diagnosed using a combination of symptoms, hormone levels, and sometimes ultrasound — not any single test. Many people with PCOS have \"normal\" ultrasounds. If your symptoms are real, push for thorough evaluation.",
  checkboxQuestionIds: [6, 9],
  romyStarters: [
    "I just took the PCOS quiz and scored mostly {tier}s. What should I know about getting properly evaluated for PCOS?",
    "Can you help me understand the connection between PCOS and insulin resistance?",
    "What questions should I bring to my doctor about irregular periods and hormonal symptoms?",
  ],
};

const pmddQuiz: QuizData = {
  slug: "pmdd",
  title: "Is It PMS — or PMDD?",
  subtitle: "A Self-Check for Premenstrual Dysphoric Disorder",
  description:
    "Most people with periods experience some premenstrual symptoms — bloating, irritability, cravings. But for 3–8% of menstruating people, the luteal phase (the ~2 weeks before your period) brings symptoms so severe they disrupt relationships, work, and mental health. That's PMDD.\n\nPMDD is not \"bad PMS.\" It's a distinct neuroendocrine condition where the brain reacts abnormally to normal hormonal changes. This quiz helps you explore whether your premenstrual experience crosses from typical PMS into PMDD territory.",
  emoji: "🌊",
  gradientFrom: "from-indigo-500",
  gradientTo: "to-purple-500",
  questions: [
    {
      id: 1,
      question: "In the 1–2 weeks before your period, how would you describe your mood?",
      options: [
        { value: "A", text: "Mostly stable — maybe a little more emotional" },
        { value: "B", text: "Noticeably more irritable or sensitive" },
        { value: "C", text: "Intense mood swings, sadness, or rage that feel out of character" },
        { value: "D", text: "I feel like a completely different person — hopelessness, despair, or uncontrollable anger" },
      ],
      emoji: "🌊",
    },
    {
      id: 2,
      question: "How quickly do your symptoms resolve once your period starts?",
      options: [
        { value: "A", text: "I don't notice a clear pattern" },
        { value: "B", text: "Symptoms ease gradually over a few days" },
        { value: "C", text: "I feel noticeably better within a day or two of bleeding" },
        { value: "D", text: "It's like a switch flips — dramatic relief within hours of my period starting" },
      ],
      emoji: "🔄",
    },
    {
      id: 3,
      question: "Do your premenstrual symptoms interfere with your daily life?",
      options: [
        { value: "A", text: "Not really — they're manageable" },
        { value: "B", text: "Mildly — I need to adjust some plans" },
        { value: "C", text: "Significantly — I struggle to function at work, school, or home" },
        { value: "D", text: "Severely — I've missed work, damaged relationships, or felt unable to cope" },
      ],
      emoji: "📉",
    },
    {
      id: 4,
      question: "Do you experience premenstrual anxiety or panic?",
      options: [
        { value: "A", text: "No more than usual" },
        { value: "B", text: "Mild increase in worry or tension" },
        { value: "C", text: "Significant anxiety that disrupts sleep or concentration" },
        { value: "D", text: "Panic attacks, racing thoughts, or overwhelming dread" },
      ],
      emoji: "😰",
    },
    {
      id: 5,
      question: "Which physical symptoms intensify before your period? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Extreme fatigue or feeling \"drugged\"" },
        { value: "symptoms", text: "Breast tenderness or swelling" },
        { value: "symptoms", text: "Bloating so severe you look pregnant" },
        { value: "symptoms", text: "Insomnia or hypersomnia" },
        { value: "symptoms", text: "Joint or muscle pain" },
        { value: "symptoms", text: "Food cravings or binge eating" },
      ],
      emoji: "🤕",
      isCheckbox: true,
    },
    {
      id: 6,
      question: "Have your premenstrual symptoms ever led to thoughts of self-harm or not wanting to be alive?",
      options: [
        { value: "A", text: "Never" },
        { value: "B", text: "Fleeting thoughts once or twice" },
        { value: "C", text: "Recurring dark thoughts during the luteal phase" },
        { value: "D", text: "Yes — and they resolve once my period starts, which is confusing and scary" },
      ],
      emoji: "🫂",
    },
    {
      id: 7,
      question: "How do the people around you react to your premenstrual changes?",
      options: [
        { value: "A", text: "They don't notice much" },
        { value: "B", text: "They mention I seem more stressed or tired" },
        { value: "C", text: "Partners, friends, or family walk on eggshells around me" },
        { value: "D", text: "My relationships have been seriously strained or damaged by my luteal-phase behavior" },
      ],
      emoji: "👥",
    },
    {
      id: 8,
      question: "Have you experienced any of the following? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Being diagnosed with depression or anxiety that only worsens premenstrually" },
        { value: "symptoms", text: "Antidepressants that help more in the luteal phase than the rest of the month" },
        { value: "symptoms", text: "Feeling \"crazy\" or \"broken\" in a cyclical pattern" },
        { value: "symptoms", text: "Tracking your symptoms and seeing a clear luteal-phase pattern" },
      ],
      emoji: "📋",
      isCheckbox: true,
    },
    {
      id: 9,
      question: "How many cycles in the past year have included severe premenstrual symptoms?",
      options: [
        { value: "A", text: "None or one — it was a one-off" },
        { value: "B", text: "2–4 cycles" },
        { value: "C", text: "Most cycles — 6 or more" },
        { value: "D", text: "Nearly every single cycle" },
      ],
      emoji: "🗓️",
    },
    {
      id: 10,
      question: "When you hear about PMDD, how do you feel?",
      options: [
        { value: "A", text: "Curious — I'm just learning about it" },
        { value: "B", text: "Somewhat relatable but not sure it fits" },
        { value: "C", text: "Deeply seen — like someone finally named what I go through" },
        { value: "D", text: "Emotional — I've been suffering without knowing this was a real condition" },
      ],
      emoji: "💡",
    },
  ],
  resultTiers: [
    {
      key: "A",
      title: "Mostly A's",
      emoji: "🌱",
      summary:
        "Your premenstrual experience sounds within the typical PMS range. If it ever worsens, tracking your symptoms by cycle day will help you spot changes early.",
      detail:
        "Mild premenstrual symptoms are common and don't necessarily point to PMDD. But if you notice a shift — symptoms becoming more intense, lasting longer, or impacting your functioning — a daily mood tracker aligned to your cycle can reveal patterns worth discussing.",
    },
    {
      key: "B",
      title: "Mostly B's",
      emoji: "🌸",
      summary:
        "You may experience moderate PMS or early PMDD-pattern symptoms. Tracking mood and symptoms by cycle day for 2–3 months will clarify the picture.",
      detail:
        "The line between severe PMS and PMDD can be blurry. What distinguishes PMDD is the degree of impairment — not just discomfort, but functional disruption. If your symptoms are trending upward, a provider familiar with PMDD can help you evaluate further.",
    },
    {
      key: "C",
      title: "Mostly C's",
      emoji: "🔥",
      summary:
        "Your symptom pattern is consistent with PMDD. The cyclical nature and functional impact you describe go beyond typical PMS.",
      detail:
        "C-dominant patterns often indicate a true PMDD presentation — mood symptoms that are time-locked to the luteal phase and significantly impact daily life. Prospective daily tracking (like the DRSP scale) over 2+ cycles is the gold standard for diagnosis. Seek a provider who knows PMDD is real and treatable.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which are commonly seen in PMDD and worth documenting for your provider.",
    },
    {
      key: "D",
      title: "Mostly D's",
      emoji: "🚨",
      summary:
        "Your experience strongly aligns with severe PMDD. The level of disruption you describe is not normal PMS, and you deserve specialized care.",
      detail:
        "A D-dominant pattern suggests significant neuroendocrine sensitivity to hormonal fluctuations. PMDD has effective treatments — including SSRIs (even luteal-phase-only dosing), hormonal approaches, and lifestyle strategies. You are not \"overreacting\" and this is not \"just hormones.\" Seek a provider who takes PMDD seriously.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which reinforce a severe PMDD pattern that warrants prompt evaluation and treatment.",
    },
  ],
  nextSteps: [
    "Track daily mood, energy, and symptoms by cycle day for at least 2 complete cycles",
    "Use a validated tool like the DRSP (Daily Record of Severity of Problems) for tracking",
    "Seek a provider experienced in PMDD — many OB-GYNs and psychiatrists are unfamiliar with it",
    "Know that effective treatments exist: SSRIs, hormonal therapies, and lifestyle interventions can help significantly",
  ],
  importantNote:
    "If you experience suicidal thoughts during the luteal phase, please reach out to the 988 Suicide & Crisis Lifeline (call or text 988). Luteal-phase suicidality is a recognized feature of severe PMDD and is treatable. You are not overreacting.",
  checkboxQuestionIds: [5, 8],
  romyStarters: [
    "I just took the PMDD quiz and scored mostly {tier}s. Can you help me understand the difference between PMS and PMDD?",
    "What treatments are available for PMDD and how do I talk to my doctor about it?",
    "Can you help me set up a daily tracking plan to document my luteal-phase symptoms?",
  ],
};

const perimenopauseQuiz: QuizData = {
  slug: "perimenopause",
  title: "Could You Be in Perimenopause?",
  subtitle: "A Self-Check for the Menopausal Transition",
  description:
    "Perimenopause — the transition leading to menopause — can start as early as your mid-30s and last 4–10 years. But many people don't recognize it because symptoms like irregular periods, sleep disruption, mood changes, and brain fog get attributed to \"stress\" or \"aging.\"\n\nPerimenopause is not just about hot flashes. It's a hormonal shift that affects your brain, bones, heart, metabolism, and mental health. This quiz helps you explore whether perimenopause might explain what you're experiencing.",
  emoji: "🦋",
  gradientFrom: "from-amber-500",
  gradientTo: "to-orange-500",
  questions: [
    {
      id: 1,
      question: "Have your periods changed in the last 1–2 years?",
      options: [
        { value: "A", text: "No — they're the same as always" },
        { value: "B", text: "Slightly different — a little shorter, longer, or lighter" },
        { value: "C", text: "Noticeably irregular — timing, flow, or duration keeps changing" },
        { value: "D", text: "Dramatically different — skipping months, flooding, or very unpredictable" },
      ],
      emoji: "📅",
    },
    {
      id: 2,
      question: "Do you experience hot flashes or night sweats?",
      options: [
        { value: "A", text: "Never" },
        { value: "B", text: "Occasionally — mild warmth or flushing" },
        { value: "C", text: "Regularly — they interrupt my comfort or sleep" },
        { value: "D", text: "Frequently — I wake up drenched or have multiple episodes daily" },
      ],
      emoji: "🔥",
    },
    {
      id: 3,
      question: "How is your sleep?",
      options: [
        { value: "A", text: "I sleep well most nights" },
        { value: "B", text: "I wake up once or twice but fall back asleep" },
        { value: "C", text: "I struggle with falling asleep, staying asleep, or waking too early" },
        { value: "D", text: "My sleep is seriously disrupted — I wake exhausted most mornings" },
      ],
      emoji: "🌙",
    },
    {
      id: 4,
      question: "How would you describe your mood recently?",
      options: [
        { value: "A", text: "Stable and generally positive" },
        { value: "B", text: "Slightly more irritable or emotional than usual" },
        { value: "C", text: "Noticeable mood swings, anxiety, or low mood that feel new or different" },
        { value: "D", text: "Significant depression, rage, or anxiety that feels hormonal and out of character" },
      ],
      emoji: "🧠",
    },
    {
      id: 5,
      question: "Have you noticed changes in your cognitive function?",
      options: [
        { value: "A", text: "No — thinking clearly as always" },
        { value: "B", text: "Occasional forgetfulness or word-finding difficulty" },
        { value: "C", text: "Regular brain fog — trouble concentrating, remembering, or multitasking" },
        { value: "D", text: "Cognitive changes that worry me or affect my work performance" },
      ],
      emoji: "🧩",
    },
    {
      id: 6,
      question: "Have you experienced any of the following? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Vaginal dryness or painful sex" },
        { value: "symptoms", text: "Increased urinary urgency or UTIs" },
        { value: "symptoms", text: "Joint aches or stiffness (new or worsening)" },
        { value: "symptoms", text: "Heart palpitations" },
        { value: "symptoms", text: "Dry skin, thinning hair, or brittle nails" },
        { value: "symptoms", text: "Changes in body odor" },
      ],
      emoji: "🔎",
      isCheckbox: true,
    },
    {
      id: 7,
      question: "What's happening with your weight and metabolism?",
      options: [
        { value: "A", text: "No real changes" },
        { value: "B", text: "Slight weight gain or shift in where I carry weight" },
        { value: "C", text: "Noticeable weight gain around my midsection despite no lifestyle changes" },
        { value: "D", text: "Significant metabolic shifts — I feel like my body doesn't respond the way it used to" },
      ],
      emoji: "⚖️",
    },
    {
      id: 8,
      question: "How old are you?",
      options: [
        { value: "A", text: "Under 35" },
        { value: "B", text: "35–40" },
        { value: "C", text: "40–47" },
        { value: "D", text: "48 or older" },
      ],
      emoji: "🎂",
    },
    {
      id: 9,
      question: "Have any of these applied to your healthcare experience? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Being told \"you're too young for menopause\"" },
        { value: "symptoms", text: "Symptoms blamed on stress, anxiety, or depression alone" },
        { value: "symptoms", text: "Offered antidepressants without hormonal evaluation" },
        { value: "symptoms", text: "Blood work that came back \"normal\" despite feeling terrible" },
      ],
      emoji: "👩‍⚕️",
      isCheckbox: true,
    },
    {
      id: 10,
      question: "When you read about perimenopause, how do you feel?",
      options: [
        { value: "A", text: "Curious — learning something new" },
        { value: "B", text: "Hmm — a few things ring true" },
        { value: "C", text: "Relief — this could explain a lot of what I've been experiencing" },
        { value: "D", text: "Validated — I've been struggling and no one connected the dots" },
      ],
      emoji: "🦋",
    },
  ],
  resultTiers: [
    {
      key: "A",
      title: "Mostly A's",
      emoji: "🌱",
      summary:
        "Your answers don't strongly suggest perimenopause right now. That said, it's good to be informed — the transition can start earlier than most people expect.",
      detail:
        "Even without current perimenopausal symptoms, knowing what to watch for is empowering. If symptoms emerge, early recognition leads to better management. Keep tracking your cycles and any changes you notice.",
    },
    {
      key: "B",
      title: "Mostly B's",
      emoji: "🌸",
      summary:
        "You may be in early perimenopause or experiencing subtle hormonal shifts. Tracking your cycles and symptoms can help you spot the transition as it unfolds.",
      detail:
        "Early perimenopause often starts with subtle cycle changes and mild symptoms that are easy to attribute to stress or lifestyle. A baseline hormone panel (FSH, estradiol, AMH) and symptom tracking can help establish where you are in the transition.",
    },
    {
      key: "C",
      title: "Mostly C's",
      emoji: "🔥",
      summary:
        "Your symptom pattern is consistent with active perimenopause. Multiple systems are being affected by hormonal fluctuations, and you deserve support.",
      detail:
        "A C-dominant pattern suggests you're experiencing the kind of multi-system disruption that characterizes active perimenopause — cycle changes, vasomotor symptoms, mood shifts, and cognitive effects. Seek a provider knowledgeable about menopausal hormone therapy and perimenopause management. You have options.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which are commonly associated with perimenopause and worth discussing with a menopause-informed provider.",
    },
    {
      key: "D",
      title: "Mostly D's",
      emoji: "🚨",
      summary:
        "Your experience strongly suggests significant perimenopausal changes that are impacting your quality of life. Many people in this stage feel dismissed — you deserve comprehensive hormonal evaluation.",
      detail:
        "A D-dominant pattern indicates substantial hormonal disruption affecting multiple areas of your life. Menopausal hormone therapy (MHT), when appropriate, can be transformative. Seek a NAMS-certified menopause practitioner or provider who stays current on menopause care. The outdated fear of HRT has left many people suffering unnecessarily.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which reinforce a significant perimenopausal pattern that warrants thorough evaluation and treatment.",
    },
  ],
  nextSteps: [
    "Track your cycle length, flow changes, and symptoms for 3+ months",
    "Request a hormonal panel: FSH, estradiol, AMH, thyroid (TSH, free T4), and consider DHEA-S",
    "Seek a menopause-informed provider or NAMS-certified practitioner",
    "Know that hormone therapy is safe and effective for most people when started during perimenopause",
  ],
  importantNote:
    "Perimenopause is a clinical diagnosis based on symptoms and age — there is no single definitive test. \"Normal\" hormone levels do not rule out perimenopause because hormones fluctuate wildly during this transition. Your symptoms are valid even if your labs look fine.",
  checkboxQuestionIds: [6, 9],
  romyStarters: [
    "I just took the perimenopause quiz and scored mostly {tier}s. What should I know about this stage of life?",
    "Can you help me understand the difference between perimenopause and menopause, and what treatments are available?",
    "What questions should I bring to my doctor about my changing cycles and new symptoms?",
  ],
};

const thyroidQuiz: QuizData = {
  slug: "thyroid",
  title: "Could Your Thyroid Be Off?",
  subtitle: "A Self-Check for Thyroid Dysfunction",
  description:
    "Your thyroid is a small butterfly-shaped gland in your neck that controls your metabolism, energy, temperature, weight, mood, and menstrual cycle. Thyroid disorders affect an estimated 1 in 8 women, yet they're frequently missed or undertreated — especially when only TSH is tested.\n\nBoth hypothyroidism (underactive) and hyperthyroidism (overactive) can mimic other conditions, which is why thyroid problems often go undiagnosed. This quiz helps you spot patterns that could point to thyroid dysfunction.",
  emoji: "🦋",
  gradientFrom: "from-teal-500",
  gradientTo: "to-cyan-500",
  questions: [
    {
      id: 1,
      question: "How would you describe your energy levels?",
      options: [
        { value: "A", text: "Good — I have enough energy for my day" },
        { value: "B", text: "A bit low — I could use more energy but I get by" },
        { value: "C", text: "Persistently fatigued despite adequate sleep" },
        { value: "D", text: "Bone-deep exhaustion that no amount of rest fixes" },
      ],
      emoji: "⚡",
    },
    {
      id: 2,
      question: "What's happening with your weight?",
      options: [
        { value: "A", text: "Stable — no unexplained changes" },
        { value: "B", text: "Minor fluctuations, nothing dramatic" },
        { value: "C", text: "Unexplained weight gain or inability to lose weight despite effort" },
        { value: "D", text: "Significant unexplained weight change (gain or loss) that feels metabolic" },
      ],
      emoji: "⚖️",
    },
    {
      id: 3,
      question: "How do you handle temperature?",
      options: [
        { value: "A", text: "I'm comfortable in most environments" },
        { value: "B", text: "I run a little cold or warm, but it's manageable" },
        { value: "C", text: "I'm always cold (or always hot) when others are comfortable" },
        { value: "D", text: "Extreme temperature sensitivity — freezing hands/feet, or overheating and sweating easily" },
      ],
      emoji: "🌡️",
    },
    {
      id: 4,
      question: "How is your hair?",
      options: [
        { value: "A", text: "Normal — no noticeable changes" },
        { value: "B", text: "Slightly drier or thinner than it used to be" },
        { value: "C", text: "Noticeable thinning, shedding, or texture changes" },
        { value: "D", text: "Significant hair loss — losing handfuls, or losing the outer third of my eyebrows" },
      ],
      emoji: "💇",
    },
    {
      id: 5,
      question: "Have your periods changed?",
      options: [
        { value: "A", text: "No — cycles are regular and normal flow" },
        { value: "B", text: "Slightly heavier, lighter, or less predictable" },
        { value: "C", text: "Noticeably heavier, more frequent, or more irregular" },
        { value: "D", text: "Dramatically different — very heavy bleeding, skipped periods, or cycle chaos" },
      ],
      emoji: "🩸",
    },
    {
      id: 6,
      question: "Do you experience any of the following? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Dry skin or brittle nails" },
        { value: "symptoms", text: "Constipation or sluggish digestion" },
        { value: "symptoms", text: "Hoarse voice or feeling of throat fullness" },
        { value: "symptoms", text: "Puffy face, especially in the morning" },
        { value: "symptoms", text: "High cholesterol despite a healthy diet" },
        { value: "symptoms", text: "Muscle aches or joint stiffness" },
      ],
      emoji: "🔎",
      isCheckbox: true,
    },
    {
      id: 7,
      question: "How is your mood and mental clarity?",
      options: [
        { value: "A", text: "Clear-headed and emotionally stable" },
        { value: "B", text: "Occasional brain fog or mild mood changes" },
        { value: "C", text: "Persistent brain fog, depression, or anxiety" },
        { value: "D", text: "Severe cognitive impairment, depression, or anxiety that feels physical, not situational" },
      ],
      emoji: "🧠",
    },
    {
      id: 8,
      question: "Is there thyroid disease or autoimmune disease in your family?",
      options: [
        { value: "A", text: "Not that I know of" },
        { value: "B", text: "A distant relative with thyroid issues" },
        { value: "C", text: "A parent or sibling with thyroid disease" },
        { value: "D", text: "Multiple family members with thyroid or autoimmune conditions" },
      ],
      emoji: "👨‍👩‍👧",
    },
    {
      id: 9,
      question: "Have you experienced any of the following? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Being told your TSH is \"normal\" despite ongoing symptoms" },
        { value: "symptoms", text: "Only having TSH tested (not free T4, free T3, or antibodies)" },
        { value: "symptoms", text: "Symptoms starting after pregnancy, major stress, or illness" },
        { value: "symptoms", text: "Being told your symptoms are \"just anxiety\" or \"just depression\"" },
      ],
      emoji: "👩‍⚕️",
      isCheckbox: true,
    },
    {
      id: 10,
      question: "How long have you been experiencing these symptoms?",
      options: [
        { value: "A", text: "A few weeks — just noticing them" },
        { value: "B", text: "A few months" },
        { value: "C", text: "6 months to 2 years" },
        { value: "D", text: "Years — it's been a slow, grinding decline" },
      ],
      emoji: "🕰️",
    },
  ],
  resultTiers: [
    {
      key: "A",
      title: "Mostly A's",
      emoji: "🌱",
      summary:
        "Your answers don't strongly suggest thyroid dysfunction right now. If you develop new symptoms, a thyroid panel is a simple place to start.",
      detail:
        "Even without a clear thyroid pattern, baseline thyroid values (TSH, free T4) are worth having on file. Thyroid conditions can develop gradually, so knowing your baseline helps detect changes early.",
    },
    {
      key: "B",
      title: "Mostly B's",
      emoji: "🌸",
      summary:
        "You may have subclinical or early thyroid changes worth monitoring. A comprehensive thyroid panel could provide peace of mind or catch an issue early.",
      detail:
        "Subtle symptoms like mild fatigue, slight weight shifts, or minor hair changes can be the earliest signs of thyroid dysfunction — especially if they're slowly worsening. Request a full panel, not just TSH.",
    },
    {
      key: "C",
      title: "Mostly C's",
      emoji: "🔥",
      summary:
        "Your symptom pattern is consistent with thyroid dysfunction. Multiple body systems seem affected in a way that points to metabolic or hormonal imbalance.",
      detail:
        "A C-dominant pattern suggests meaningful thyroid-related symptoms across energy, weight, temperature, hair, mood, and cycle. Request a comprehensive thyroid panel: TSH, free T4, free T3, TPO antibodies, and thyroglobulin antibodies. \"Normal\" TSH alone does not rule out thyroid disease.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which are classically associated with thyroid dysfunction and should be shared with your provider.",
    },
    {
      key: "D",
      title: "Mostly D's",
      emoji: "🚨",
      summary:
        "Your answers strongly suggest significant thyroid dysfunction that is affecting your quality of life. Many people with similar patterns have been undertreated or told their labs are \"fine.\" Push for thorough testing.",
      detail:
        "A D-dominant pattern points to substantial metabolic disruption consistent with thyroid disease — possibly Hashimoto's thyroiditis, the most common cause of hypothyroidism. Seek an endocrinologist or provider who tests beyond TSH and is willing to treat based on symptoms, not just lab ranges. Optimal is not the same as \"normal range.\"",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which reinforce a significant thyroid pattern that warrants comprehensive evaluation beyond TSH alone.",
    },
  ],
  nextSteps: [
    "Request a comprehensive thyroid panel: TSH, free T4, free T3, TPO antibodies, and thyroglobulin antibodies",
    "Track your symptoms, energy, weight, and temperature patterns over time",
    "Know that \"normal\" TSH does not rule out thyroid disease — optimal ranges are narrower than lab reference ranges",
    "If you have a family history of autoimmune disease, antibody testing is especially important",
  ],
  importantNote:
    "Thyroid disease is one of the most treatable hormonal conditions when properly diagnosed. If your symptoms are real but your TSH is \"normal,\" you may need a more complete panel or a provider who looks at the full picture. Do not let a single lab value invalidate your experience.",
  checkboxQuestionIds: [6, 9],
  romyStarters: [
    "I just took the thyroid quiz and scored mostly {tier}s. What comprehensive thyroid tests should I request?",
    "Can you help me understand the difference between hypothyroidism and Hashimoto's thyroiditis?",
    "What should I know about thyroid lab ranges and why \"normal\" doesn't always mean \"optimal\"?",
  ],
};

const fibroidsQuiz: QuizData = {
  slug: "fibroids",
  title: "Understanding Your Heavy Periods",
  subtitle: "A Self-Check for Uterine Fibroids",
  description:
    "Uterine fibroids are noncancerous growths in or on the uterus that affect up to 80% of women by age 50, with Black women disproportionately affected. Despite being incredibly common, fibroids are often underdiagnosed or dismissed until symptoms become severe.\n\nHeavy periods, pelvic pressure, frequent urination, and fatigue from blood loss are not things you should just \"live with.\" This quiz helps you explore whether fibroids — or another cause of heavy bleeding — might be worth investigating.",
  emoji: "🩺",
  gradientFrom: "from-rose-500",
  gradientTo: "to-red-500",
  questions: [
    {
      id: 1,
      question: "How would you describe your menstrual flow?",
      options: [
        { value: "A", text: "Light to moderate — lasts 3–5 days" },
        { value: "B", text: "Moderate — I use regular products and change them on schedule" },
        { value: "C", text: "Heavy — I go through pads/tampons quickly or double up" },
        { value: "D", text: "Very heavy — flooding, soaking through products hourly, or passing large clots" },
      ],
      emoji: "🩸",
    },
    {
      id: 2,
      question: "How long does your period typically last?",
      options: [
        { value: "A", text: "3–5 days" },
        { value: "B", text: "5–6 days" },
        { value: "C", text: "7–8 days" },
        { value: "D", text: "8+ days, or I have breakthrough bleeding between periods" },
      ],
      emoji: "📅",
    },
    {
      id: 3,
      question: "Do you pass blood clots during your period?",
      options: [
        { value: "A", text: "Rarely or never" },
        { value: "B", text: "Small clots occasionally" },
        { value: "C", text: "Clots the size of a coin regularly" },
        { value: "D", text: "Large clots (golf ball–sized or bigger) that are alarming" },
      ],
      emoji: "🔴",
    },
    {
      id: 4,
      question: "Do you experience pelvic pain or pressure?",
      options: [
        { value: "A", text: "No pelvic pain or pressure" },
        { value: "B", text: "Mild cramping during my period" },
        { value: "C", text: "Pelvic pressure or heaviness, especially before my period" },
        { value: "D", text: "Chronic pelvic pressure, lower back pain, or a feeling of fullness in my abdomen" },
      ],
      emoji: "💥",
    },
    {
      id: 5,
      question: "Do you experience any of these \"bulk\" symptoms? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Frequent urination or difficulty emptying your bladder" },
        { value: "symptoms", text: "Constipation or feeling of rectal pressure" },
        { value: "symptoms", text: "Lower belly looking bloated or distended" },
        { value: "symptoms", text: "Pain during sex, especially with deep penetration" },
        { value: "symptoms", text: "Feeling like something is pressing on your organs" },
      ],
      emoji: "🔎",
      isCheckbox: true,
    },
    {
      id: 6,
      question: "How does your period affect your daily life?",
      options: [
        { value: "A", text: "Barely notice it — life goes on as normal" },
        { value: "B", text: "Some inconvenience but I manage" },
        { value: "C", text: "I plan my life around my period — avoiding travel, events, or white clothing" },
        { value: "D", text: "My period controls my life — I've missed work, cancelled plans, or had accidents" },
      ],
      emoji: "📉",
    },
    {
      id: 7,
      question: "Do you experience fatigue or signs of anemia?",
      options: [
        { value: "A", text: "I feel energetic and well" },
        { value: "B", text: "A little tired around my period" },
        { value: "C", text: "Persistent fatigue, paleness, or shortness of breath with exertion" },
        { value: "D", text: "Severe fatigue, dizziness, heart palpitations, or I've been told I'm anemic" },
      ],
      emoji: "😴",
    },
    {
      id: 8,
      question: "Have you experienced any of these? (Check all that apply)",
      options: [
        { value: "symptoms", text: "Being told heavy periods are \"normal for you\"" },
        { value: "symptoms", text: "Iron deficiency or anemia requiring supplements or infusions" },
        { value: "symptoms", text: "Fibroids found incidentally on imaging" },
        { value: "symptoms", text: "Being offered a hysterectomy as the first or only option" },
        { value: "symptoms", text: "Symptoms worsening in your 30s or 40s" },
      ],
      emoji: "👩‍⚕️",
      isCheckbox: true,
    },
    {
      id: 9,
      question: "How has your bleeding changed over time?",
      options: [
        { value: "A", text: "It's been consistent since my teens" },
        { value: "B", text: "Gradually heavier over the past few years" },
        { value: "C", text: "Noticeably heavier or longer than it was 2–5 years ago" },
        { value: "D", text: "Dramatically worse — what used to be manageable is now overwhelming" },
      ],
      emoji: "📈",
    },
    {
      id: 10,
      question: "Has anyone in your family had fibroids, heavy periods, or a hysterectomy?",
      options: [
        { value: "A", text: "Not that I know of" },
        { value: "B", text: "A distant relative" },
        { value: "C", text: "My mother or sister had fibroids or heavy periods" },
        { value: "D", text: "Multiple women in my family — it seems to run in my family" },
      ],
      emoji: "👨‍👩‍👧",
    },
  ],
  resultTiers: [
    {
      key: "A",
      title: "Mostly A's",
      emoji: "🌱",
      summary:
        "Your bleeding patterns sound within the typical range. If things change over time, tracking your flow and symptoms will help you catch any shifts early.",
      detail:
        "Normal menstrual blood loss is 30–80 mL per cycle. If you're comfortable with your periods and not experiencing fatigue or other symptoms, you're likely in a good place. Keep monitoring, especially as you age — fibroids can develop or grow over time.",
    },
    {
      key: "B",
      title: "Mostly B's",
      emoji: "🌸",
      summary:
        "You may have mildly heavy periods worth monitoring. If bleeding is increasing over time, a baseline evaluation could catch issues early.",
      detail:
        "Mildly heavy periods can be normal for some people, but a gradual increase is worth tracking. Consider a complete blood count (CBC) to check for anemia, and a pelvic ultrasound if symptoms worsen. Early detection of fibroids gives you more treatment options.",
    },
    {
      key: "C",
      title: "Mostly C's",
      emoji: "🔥",
      summary:
        "Your bleeding pattern and associated symptoms are consistent with uterine fibroids or another structural cause of heavy periods. Further evaluation is warranted.",
      detail:
        "A C-dominant pattern suggests clinically significant heavy menstrual bleeding with possible bulk symptoms. Request a pelvic ultrasound (transvaginal gives the best detail), a CBC to assess for anemia, and iron studies. Multiple treatment options exist beyond hysterectomy — including medication, procedures like UAE, and myomectomy.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which are commonly associated with fibroids and should be shared with your provider.",
    },
    {
      key: "D",
      title: "Mostly D's",
      emoji: "🚨",
      summary:
        "Your experience strongly suggests significant heavy menstrual bleeding, possibly from fibroids, that is impacting your health and quality of life. You deserve comprehensive evaluation and treatment — not just being told to \"deal with it.\"",
      detail:
        "A D-dominant pattern indicates heavy bleeding that is likely causing anemia and significantly affecting your daily life. Seek a gynecologist experienced in fibroid management who will discuss the full range of treatment options: medical management, uterine artery embolization (UAE), myomectomy, focused ultrasound, and more. Hysterectomy is not the only answer.",
      symptomNote:
        "You reported {count} additional symptom(s) ({examples}), which reinforce a significant bleeding and fibroid pattern requiring prompt evaluation.",
    },
  ],
  nextSteps: [
    "Request a pelvic ultrasound (transvaginal preferred) to evaluate for fibroids and other structural causes",
    "Get a complete blood count (CBC) and iron studies to check for anemia",
    "Track your bleeding: days, flow volume, clot size, and impact on daily activities",
    "Ask about ALL treatment options — medication, procedures, and surgery — not just hysterectomy",
  ],
  importantNote:
    "Heavy periods are common but not \"normal\" when they interfere with your life, cause anemia, or worsen over time. Black women are disproportionately affected by fibroids and often receive delayed diagnosis. If your bleeding is impacting your quality of life, you deserve evaluation regardless of your age.",
  checkboxQuestionIds: [5, 8],
  romyStarters: [
    "I just took the fibroids quiz and scored mostly {tier}s. What should I know about getting evaluated for heavy periods?",
    "Can you help me understand the treatment options for fibroids beyond hysterectomy?",
    "What questions should I bring to my gynecologist about my heavy bleeding and possible anemia?",
  ],
};

export const QUIZZES: QuizData[] = [
  adrenalFatigueQuiz,
  endometriosisQuiz,
  pcosQuiz,
  pmddQuiz,
  perimenopauseQuiz,
  thyroidQuiz,
  fibroidsQuiz,
];

export function getQuizBySlug(slug: string): QuizData | undefined {
  return QUIZZES.find((q) => q.slug === slug);
}
